import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@1trading/react';
import { useState } from 'react';
import { FieldError } from '@/components/ui/field-error';
import { FormSection } from '@/components/ui/form-section';
import { StatusChip } from '@/components/ui/status-chip';
import { DOWNTIME_PREVIEW_DAYS, useMmProgramDowntime } from '@/hooks/queries/mm-programs/use-mm-programs';
import { pluralize } from '@/utils/string';
import { DowntimeRuleDialog } from './downtime-rule-dialog';
import { blankDowntimeRule, describeDowntimeRule } from './mm-program-editing';
import type { DowntimeRuleInput } from './mm-program-schema';

const MS_PER_MINUTE = 60 * 1000;

/** What the saved rules resolve to; unsaved edits in the table above are not included until the settings are saved. */
const SavedDowntimePreview = ({ pid, instrument }: { pid: string; instrument: string }) => {
  const preview = useMmProgramDowntime(pid, instrument);
  if (!preview.data) return null;
  const { windows, downtimeMs } = preview.data;
  return (
    <p className="text-xs text-muted-foreground">
      Saved rules, next {DOWNTIME_PREVIEW_DAYS} days:{' '}
      {windows.length ? `${pluralize(windows.length, 'window')}, ${pluralize(Math.round(downtimeMs / MS_PER_MINUTE), 'minute')} in total.` : 'no downtime.'}
    </p>
  );
};
SavedDowntimePreview.displayName = 'SavedDowntimePreview';

interface DowntimeSectionProps {
  pid: string;
  instrument: string;
  rules: DowntimeRuleInput[];
  canEdit: boolean;
  /** A problem with the instrument's rules as a whole, such as a duplicate id */
  error?: string;
  onChange: (rules: DowntimeRuleInput[]) => void;
}

export const DowntimeSection = ({ pid, instrument, rules, canEdit, error, onChange }: DowntimeSectionProps) => {
  const [editing, setEditing] = useState<DowntimeRuleInput | null>(null);
  const isNew = (rule: DowntimeRuleInput) => !rules.some(({ id }) => id === rule.id);

  const save = (draft: DowntimeRuleInput) => {
    onChange(isNew(draft) ? [...rules, draft] : rules.map((rule) => (rule.id === draft.id ? draft : rule)));
    setEditing(null);
  };

  return (
    <FormSection title="Scheduled downtime">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead>When</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>Status</TableHead>
            {canEdit ? <TableHead className="w-40" /> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rules.length ? (
            rules.map((rule) => (
              <TableRow key={rule.id}>
                <TableCell>{rule.type === 'recurring' ? 'Recurring' : 'One-off'}</TableCell>
                <TableCell className="font-mono text-xs">{describeDowntimeRule(rule)}</TableCell>
                <TableCell>{rule.reason}</TableCell>
                <TableCell>
                  <StatusChip variant={rule.enabled ? 'success' : 'muted'}>{rule.enabled ? 'Enabled' : 'Disabled'}</StatusChip>
                </TableCell>
                {canEdit ? (
                  <TableCell className="space-x-2 text-right">
                    <Button onClick={() => setEditing(rule)} size="sm" type="button" variant="outline">
                      Edit
                    </Button>
                    <Button onClick={() => onChange(rules.filter(({ id }) => id !== rule.id))} size="sm" type="button" variant="ghost">
                      Delete
                    </Button>
                  </TableCell>
                ) : null}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell className="text-muted-foreground" colSpan={canEdit ? 5 : 4}>
                No downtime rules for this market.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <FieldError message={error} />
      {canEdit ? (
        <div className="flex gap-2">
          <Button onClick={() => setEditing(blankDowntimeRule('recurring'))} size="sm" type="button" variant="outline">
            Add recurring
          </Button>
          <Button onClick={() => setEditing(blankDowntimeRule('one_off'))} size="sm" type="button" variant="outline">
            Add one-off
          </Button>
        </div>
      ) : null}
      <SavedDowntimePreview instrument={instrument} pid={pid} />
      {editing ? <DowntimeRuleDialog isNew={isNew(editing)} onClose={() => setEditing(null)} onSave={save} rule={editing} /> : null}
    </FormSection>
  );
};
DowntimeSection.displayName = 'DowntimeSection';
